import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit2, 
  Trash2,
  Users,
  Search,
  Building2,
  Mail,
  Phone,
  Shield,
  Key,
  ToggleLeft,
  ToggleRight,
  AlertTriangle
} from 'lucide-react';
import toast from 'react-hot-toast';
import AdminLayout from '../../components/layout/AdminLayout';
import Modal from '../../components/ui/Modal';
import LoadingSpinner from '../../components/ui/LoadingSpinner';
import { useAuth } from '../../context/AuthContext';
import { adminsAPI, stationsAPI } from '../../services/api';

const AdminsManagement = () => {
  const { admin: currentAdmin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [stations, setStations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [adminToDelete, setAdminToDelete] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    role: 'station_admin',
    assignedStation: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [adminsRes, stationsRes] = await Promise.all([
        adminsAPI.getAll(),
        stationsAPI.getAll()
      ]);
      setAdmins(adminsRes.data.admins);
      setStations(stationsRes.data.stations);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (admin = null) => {
    if (admin) {
      setEditingAdmin(admin);
      setFormData({
        name: admin.name,
        email: admin.email,
        mobile: admin.mobile,
        password: '',
        role: admin.role,
        assignedStation: admin.assignedStation?._id || ''
      });
    } else {
      setEditingAdmin(null);
      setFormData({
        name: '',
        email: '',
        mobile: '',
        password: '',
        role: 'station_admin',
        assignedStation: ''
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAdmin(null);
  };

  const handleOpenPasswordModal = (admin) => {
    setSelectedAdmin(admin);
    setNewPassword('');
    setIsPasswordModalOpen(true);
  };

  const handleClosePasswordModal = () => {
    setIsPasswordModalOpen(false);
    setSelectedAdmin(null);
    setNewPassword('');
  };

  const handleOpenDeleteModal = (admin) => {
    setAdminToDelete(admin);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setAdminToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDelete = async () => {
    if (!adminToDelete) return;

    try {
      await adminsAPI.delete(adminToDelete._id);
      // Remove from local state immediately
      setAdmins(prev => prev.filter(a => a._id !== adminToDelete._id));
      toast.success('Admin deleted permanently');
      handleCloseDeleteModal();
    } catch (error) {
      console.error('Error deleting admin:', error);
      toast.error(error.response?.data?.error || 'Failed to delete admin');
      // Refresh data in case of error to ensure consistency
      fetchData();
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      name: formData.name,
      email: formData.email,
      mobile: formData.mobile,
      role: formData.role,
      assignedStation: formData.assignedStation || null
    };
    
    if (!editingAdmin) {
      data.password = formData.password;
    }
    
    try {
      if (editingAdmin) {
        await adminsAPI.update(editingAdmin._id, data);
        // Update local state immediately
        setAdmins(prev => prev.map(a => 
          a._id === editingAdmin._id ? { ...a, ...data } : a
        ));
        toast.success('Admin updated successfully');
      } else {
        const response = await adminsAPI.create(data);
        // Add new admin to local state
        if (response.data.admin) {
          fetchData(); // Fetch to get fully populated data
        }
        toast.success('Admin created successfully');
      }
      
      handleCloseModal();
    } catch (error) {
      console.error('Error saving admin:', error);
      if (error.response?.status === 404) {
        setAdmins(prev => prev.filter(a => a._id !== editingAdmin?._id));
        toast.error('Admin no longer exists');
        handleCloseModal();
      } else {
        toast.error(error.response?.data?.error || 'Failed to save admin');
      }
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    try {
      await adminsAPI.resetPassword(selectedAdmin._id, newPassword);
      toast.success('Password reset successfully');
      handleClosePasswordModal();
    } catch (error) {
      console.error('Error resetting password:', error);
      if (error.response?.status === 404) {
        setAdmins(prev => prev.filter(a => a._id !== selectedAdmin._id));
        toast.error('Admin no longer exists');
        handleClosePasswordModal();
      } else {
        toast.error('Failed to reset password');
      }
    }
  };

  const handleToggleActive = async (admin) => {
    try {
      await adminsAPI.update(admin._id, { isActive: !admin.isActive });
      // Update local state immediately
      setAdmins(prev => prev.map(a => 
        a._id === admin._id ? { ...a, isActive: !a.isActive } : a
      ));
      toast.success(`Admin ${admin.isActive ? 'deactivated' : 'activated'}`);
    } catch (error) {
      console.error('Error toggling admin:', error);
      if (error.response?.status === 404) {
        // Admin doesn't exist, remove from list
        setAdmins(prev => prev.filter(a => a._id !== admin._id));
        toast.error('Admin no longer exists');
      } else {
        toast.error('Failed to update admin');
      }
      fetchData();
    }
  };

  const filteredAdmins = admins.filter((admin) => {
    // Filter out invalid/null admins
    if (!admin || !admin._id || !admin.name || !admin.email) return false;
    
    return admin.name.toLowerCase().includes(search.toLowerCase()) ||
           admin.email.toLowerCase().includes(search.toLowerCase()) ||
           admin.assignedStation?.name?.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-display font-bold text-midnight-900">
              Admin Accounts
            </h1>
            <p className="text-midnight-600 mt-1">
              Manage admin users and their permissions
            </p>
          </div>
          <button 
            onClick={() => handleOpenModal()}
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Admin
          </button>
        </div>

        {/* Search */}
        <div className="relative max-w-md">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-midnight-400" />
          <input
            type="text"
            placeholder="Search admins..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field pl-12"
          />
        </div>

        {/* Admins Table */}
        <div className="card overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <LoadingSpinner size="lg" />
            </div>
          ) : filteredAdmins.length === 0 ? (
            <div className="text-center py-20">
              <Users className="w-16 h-16 text-midnight-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-midnight-700">No admins found</h3>
              <p className="text-midnight-500 mt-2">Add a new admin to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-midnight-50 border-b border-midnight-100">
                  <tr>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Admin</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Contact</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Role</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Station</th>
                    <th className="text-left px-6 py-4 text-sm font-semibold text-midnight-600">Status</th>
                    <th className="text-right px-6 py-4 text-sm font-semibold text-midnight-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-midnight-100">
                  {filteredAdmins.map((admin) => (
                    <tr key={admin._id} className="hover:bg-midnight-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-midnight-200 rounded-full flex items-center justify-center">
                            <span className="font-bold text-midnight-600">
                              {admin.name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <div className="font-semibold text-midnight-900">{admin.name}</div>
                            <div className="text-sm text-midnight-500 flex items-center gap-1">
                              <Mail className="w-3 h-3" />
                              {admin.email}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-1 text-midnight-600">
                          <Phone className="w-4 h-4" />
                          {admin.mobile}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${
                          admin.role === 'super_admin' 
                            ? 'bg-purple-100 text-purple-700' 
                            : 'bg-blue-100 text-blue-700'
                        }`}>
                          <Shield className="w-3 h-3" />
                          {admin.role === 'super_admin' ? 'Super Admin' : 'Station Admin'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {admin.assignedStation ? (
                          <div className="flex items-center gap-2 text-midnight-600">
                            <Building2 className="w-4 h-4" />
                            <span>{admin.assignedStation.name}</span>
                            <span className="text-midnight-400">({admin.assignedStation.code})</span>
                          </div>
                        ) : (
                          <span className="text-midnight-400">All Stations</span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => handleToggleActive(admin)}
                          className={`flex items-center gap-2 ${
                            admin.isActive ? 'text-green-600' : 'text-midnight-400'
                          }`}
                        >
                          {admin.isActive ? (
                            <>
                              <ToggleRight className="w-6 h-6" />
                              <span className="text-sm font-medium">Active</span>
                            </>
                          ) : (
                            <>
                              <ToggleLeft className="w-6 h-6" />
                              <span className="text-sm font-medium">Inactive</span>
                            </>
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => handleOpenPasswordModal(admin)}
                            className="p-2 text-midnight-500 hover:text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenModal(admin)}
                            className="p-2 text-midnight-500 hover:text-railway-500 hover:bg-railway-50 rounded-lg transition-colors"
                            title="Edit Admin"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleOpenDeleteModal(admin)}
                            className={`p-2 rounded-lg transition-colors ${
                              currentAdmin?._id === admin._id
                                ? 'text-midnight-300 cursor-not-allowed'
                                : 'text-midnight-500 hover:text-red-500 hover:bg-red-50'
                            }`}
                            title={currentAdmin?._id === admin._id ? "Cannot delete yourself" : "Delete Admin"}
                            disabled={currentAdmin?._id === admin._id}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingAdmin ? 'Edit Admin' : 'Add New Admin'}
      >
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Full Name *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="e.g., John Doe"
              className="input-field"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Email *
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="e.g., admin@retiringroom.com"
              className="input-field"
              required
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Mobile *
            </label>
            <input
              type="tel"
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="e.g., 9876543210"
              className="input-field"
              maxLength={10}
              required
            />
          </div>
          
          {!editingAdmin && (
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Minimum 6 characters"
                className="input-field"
                minLength={6}
                required
              />
            </div>
          )}
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              Role *
            </label>
            <select
              name="role"
              value={formData.role}
              onChange={handleChange}
              className="input-field"
              required
            >
              <option value="station_admin">Station Admin</option>
              <option value="super_admin">Super Admin</option>
            </select>
          </div>
          
          {formData.role === 'station_admin' && (
            <div>
              <label className="block text-sm font-medium text-midnight-700 mb-2">
                Assigned Station
              </label>
              <select
                name="assignedStation"
                value={formData.assignedStation}
                onChange={handleChange}
                className="input-field"
              >
                <option value="">Select a station</option>
                {stations.map((station) => (
                  <option key={station._id} value={station._id}>
                    {station.name} ({station.code})
                  </option>
                ))}
              </select>
            </div>
          )}
          
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={handleCloseModal} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1">
              {editingAdmin ? 'Update Admin' : 'Create Admin'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Reset Password Modal */}
      <Modal
        isOpen={isPasswordModalOpen}
        onClose={handleClosePasswordModal}
        title="Reset Password"
        size="sm"
      >
        <form onSubmit={handleResetPassword} className="space-y-4">
          <p className="text-midnight-600">
            Reset password for <strong>{selectedAdmin?.name}</strong>
          </p>
          
          <div>
            <label className="block text-sm font-medium text-midnight-700 mb-2">
              New Password *
            </label>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Minimum 6 characters"
              className="input-field"
              minLength={6}
              required
            />
          </div>
          
          <div className="flex gap-3 pt-4">
            <button type="button" onClick={handleClosePasswordModal} className="btn-secondary flex-1">
              Cancel
            </button>
            <button type="submit" className="btn-primary flex-1">
              Reset Password
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        title="Delete Admin"
        size="sm"
      >
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h4 className="text-xl font-display font-semibold text-midnight-900 mb-2">
            Permanently Delete Admin?
          </h4>
          <p className="text-midnight-600 mb-4">
            Are you sure you want to permanently delete the admin account for
            <strong className="text-midnight-900"> "{adminToDelete?.name}"</strong>?
          </p>
          <p className="text-sm text-red-500 font-medium mb-6">
            This action cannot be undone. The account will be permanently removed.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={handleCloseDeleteModal}
              className="btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              className="btn-danger flex-1"
            >
              Delete Permanently
            </button>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminsManagement;
